import { throttle } from 'lodash';
import Image from 'next/image';
import { FunctionComponent, useEffect, useCallback } from 'react';
import { isIOS, browserVersion } from 'react-device-detect';

import RecommendationCard, { CardColor } from '@/components/RecommendationCard';
import { AllFavourites } from '@/services/FavouritesApi';
import { RecommendedItem } from '@/services/RecommendationApi';

interface RecommendationsProps extends RewindSlideProps {
  recommendedItems: RecommendedItem[];
  favouritesData: AllFavourites;
}

const Recommendations: FunctionComponent<RecommendationsProps> = (props) => {
  const { recommendedItems, active = false, favouritesData } = props;
  const enableFlipAnimation = !isIOS || parseInt(browserVersion, 10) >= 16;

  const spreadCard = useCallback((spread: boolean = true) => {
    recommendedItems.forEach((_item, index) => {
      const cardElement = document.getElementById(`rec-card-${index}`);
      if (cardElement) {
        switch (index) {
          case 0:
            if (window.innerWidth >= 1024) {
              cardElement.style.transform = `translateX(-50%) ${spread ? 'translateX(-330px) translateY(42px) rotate(-15deg)' : ''}`;
            } else {
              cardElement.style.transform = `${spread ? 'translateX(0px)' : 'translateX(285px)'}`;
            }
            break;

          case 1:
            if (window.innerWidth >= 1024) {
              cardElement.style.transform = '';
            } else {
              cardElement.style.transform = `${spread ? 'translateX(285px)' : 'translateX(285px)'}`;
            }
            break;

          case 2:
            if (window.innerWidth >= 1024) {
              cardElement.style.transform = `translateX(-50%) ${spread ? 'translateX(330px) translateY(42px) rotate(15deg)' : ''}`;
            } else {
              cardElement.style.transform = `${spread ? 'translateX(570px)' : 'translateX(285px)'}`;
            }
            break;

          default:
          // nothing
        }
      }
    });
  }, [recommendedItems]);

  const updateContainerStyles = useCallback(() => {
    const scrollContainer = document.getElementById('scroll-container');
    const recommendationsContainer = document.getElementById('recommendations-container');

    if (scrollContainer) {
      scrollContainer.style.width = `${window.innerWidth}px`;
    }

    if (recommendationsContainer && window.innerWidth >= 1024) {
      recommendationsContainer.style.width = 'auto';
    } else if (recommendationsContainer) {
      // Needs to set the container width to the total width of the children minus some margins.
      // This is to allow us to center the overflow scrolling prior to rendering the children in the handleRevealClick above.
      recommendationsContainer.style.width = `${((recommendationsContainer.firstChild as HTMLElement).clientWidth * 3) - 20}px`;
    }
  }, []);

  const handleRevealClick = useCallback(() => {
    const recommendationsCta = document.getElementById('recommendations-cta');
    const scrollContainer = document.getElementById('scroll-container');
    const recommendationsContainer = document.getElementById('recommendations-container');

    if (
      scrollContainer
      && recommendationsCta
      && recommendationsContainer
      && recommendationsContainer.firstChild
    ) {
      updateContainerStyles();
      recommendationsCta.style.opacity = '0';
      recommendationsCta.style.pointerEvents = 'none';
      scrollContainer.style.opacity = '1';

      // Centering the overflow scroll so that when we do the card spread animation the middle card is centred on screen.
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      scrollContainer.scrollLeft = Math.ceil(maxScrollLeft / 2);

      spreadCard(true);
    }
  }, [updateContainerStyles, spreadCard]);

  useEffect(() => {
    const handleResize = throttle(() => {
      if (active) {
        updateContainerStyles();
        spreadCard(true);
      }
    }, 250);

    window.addEventListener('resize', handleResize);

    if (!active) {
      spreadCard(false);

      const recommendationsCta = document.getElementById('recommendations-cta');
      const scrollContainer = document.getElementById('scroll-container');

      // Resets some styling to prepare for when the user comes back to this page
      if (scrollContainer && recommendationsCta) {
        recommendationsCta.style.opacity = '1';
        recommendationsCta.style.pointerEvents = 'all';
        scrollContainer.style.opacity = '0';
        scrollContainer.style.width = `${window.innerWidth}px`;
      }
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [active, spreadCard, updateContainerStyles]);

  return (
    <div className="flex flex-col items-center gap-6 lg:gap-20">
      <h2 className="text-xl xs:text-2xl md:text-3xl lg:text-4xl w-full max-w-[550px] md:max-w-[900px] lg:max-w-[1100px] m-auto slide-up">
        But what will you
        {' '}
        <span className="text-dark-tangerine">watch next?</span>
      </h2>
      <div className="relative">
        <div
          id="scroll-container"
          className="relative top-0 w-full h-full transition-all duration-1000 overflow-x-auto lg:overflow-visible"
        >
          <div id="recommendations-container" className="relative top-0 w-full h-full mx-auto">
            {recommendedItems.map((recommendedItem, index) => {
              const cardColorMap: CardColor[] = ['white', 'cream', 'orange'];

              const isFavourite = !!favouritesData[recommendedItem.entityType].find((item) => { return item === recommendedItem.id; });

              return (
                <div
                  key={recommendedItem.title}
                  id={`rec-card-${index}`}
                  className="absolute lg:left-[50%] lg:translate-x-[-50%] transition-transform duration-1000 ease-in-out"
                >
                  {active && (
                    <RecommendationCard
                      cardIndex={index}
                      cardColor={cardColorMap[index]}
                      title="movie"
                      image={`${process.env.NEXT_PUBLIC_IMAGE_SERVER_HOST}/${recommendedItem.imageId}?crop=true&width=600&height=900&type=webp`}
                      description={recommendedItem.description}
                      url={recommendedItem.url}
                      entityType={recommendedItem.entityType}
                      id={recommendedItem.id}
                      enableFlipAnimation={enableFlipAnimation}
                      isFavourite={isFavourite}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div
          id="recommendations-cta"
          className="relative top-[-400px] lg:top-[-485px] mx-auto !w-[292px] !h-[400px] lg:!w-[355px] lg:!h-[485px]
            bg-[url('/images/tarot-card-orange.png')] bg-contain text-black
            flex flex-col justify-center items-center gap-4 lg:gap-10
            slide-down"
        >
          <Image
            className="!w-[119px] !h-[100px] md:!w-[142px] md:!h-[119px]"
            src="/images/crystal-ball.png"
            alt="Crystal Ball"
            width={284}
            height={238}
          />
          <p className="text-base lg:text-lg font-ubuntu font-bold px-12">
            The SBS On Demand fortune teller can predict what you will watch in 2024
          </p>
          <button
            type="button"
            className="sbs-button sbs-button-secondary"
            onClick={handleRevealClick}
            tabIndex={active ? 0 : -1}
          >
            Reveal your fortune
          </button>
        </div>
      </div>
    </div>
  );
};

export default Recommendations;
